import { Cloudinary } from '@cloudinary/url-gen';

export const AGORA_CONTACT = '1300812345';

export const SETTINGS = {
  seo: {
    defaultTitle: process.env.NEXT_PUBLIC_NAME,
    defaultDescription: '',
    defaultImage: '',
    twitterSite: '',
  },
};

export const DATE_FORMAT = {
  /** `dd/MM/yyyy` */
  long: 'dd/MM/yyyy',
  /** `d/M/yy` */
  short: 'd/M/yy',
  /**`d MMM */
  xShort: 'd MMM',
  /** 1 Jun 2023 */
  listing: 'd LLL yyyy',
  listing_short: 'd LLL',
  /** TUE, 27 JUN */
  stockbook_week: 'EEE, d MMM',
};

export const DATETIME_FORMAT = {
  /** `dd/MM/yyyy HH:mm:ss` */
  long: 'dd/MM/yyyy HH:mm:ss',
  /** `dd/MM/yyyy HH:mm` */
  medium: 'dd/MM/yyyy HH:mm',
  /** `d/M/yy H:mm` */
  short: 'd/M/yy H:mm',
  /** yeah I dunno */
  extraLong: 'h:mmaaa do LLLL yyyy',
};

export const PATHS = {
  home: '/',
  contacts: {
    home: '/contacts',
  },
  privateGroups: {
    home: '/networks',
  },
  prices: {
    home: '/prices',
    edit: '/prices/edit',
    new: '/prices/edit/new',
  },
  summaryGrids: {
    home: '/summary-grids',
    edit: '/summary-grids/edit',
    new: '/summary-grids/edit/new',
  },
  offers: {
    home: '/offers',
    edit: '/offers/edit',
    new: '/offers/edit/new',
  },
  stockbook: {
    home: '/stockbook',
    contract: '/stockbook/contract',
    orgConfirmation: '/booking/org-confirmation/',
    orgBooking: '/booking/org-booking/',
    publicConfirmation: '/booking/confirmation/',
    bookingHistory: '/booking/history/',
    orgBookingRequest: '/booking/org-booking-request/',
  },
  picManager: {
    home: '/pic-manager',
  },
  stockRequired: {
    home: '/stockbook-required',
  },

  help: 'http://www.agoralivestock.com.au/faqs',
  auth: {
    logIn: '/auth/login',
    error: '/auth/error',
    logInVerified: '/auth/login?verified=true',
    logInNotVerified: '/auth/login?verified=false',
    signUp: '/auth/sign-up',
    verification: '/auth/verification',
    forgotPassword: '/auth/forgot-password',
    forgotPasswordConfirmation: '/auth/forgot-password-confirmation',
    resetPasswordConfirmation: '/auth/reset-password-confirmation',
  },
  api: {
    invalidateRefreshToken: '/api/auth/invalidate-refresh-token',
  },
  organisation: {
    picker: '/organisation',
    create: '/organisation/create',
    profile: '/organisation/profile',
  },
  profile: { home: '/profile' },
  marketplace: {
    listings: '/marketplace',
    buyListings: '/marketplace/my-buy-listings',
    sellListings: '/marketplace/my-sell-listings',
    publicListings: '/marketplace/listing',
    publicListing: '/marketplace/listing/[id].tsx',
    viewListings: '/marketplace/listing/view',
    createListing: '/marketplace/listing/edit/new',
    editListing: '/marketplace/listing/edit/',
    listingConfirmation: '/marketplace/listing/listings-confirmation',
    alert: '/marketplace?alertModal=true',
    listing: '/marketplace/listing/',
  },
  media: {
    home: '/media-library',
  },
  app: {
    marketplace: '/app/marketplace',
    alerts: '/app/marketplace-alerts',
    mySellListings: '/app/marketplace/my-sell-listings',
    myBuyListings: '/app/marketplace/my-buy-listings',
    listing: '/app/marketplace/listing',
    viewListing: '/app/marketplace/listing/view',
    addContact: '/app/contacts/add-contact',
  },
};

// Paths that are deemed safe to visit when a buyer org has not yet been selected
export const SAFE_PATHS_NO_ORG = [
  PATHS.profile.home,
  PATHS.organisation.picker,
  PATHS.organisation.create,
  PATHS.marketplace.listings,
  PATHS.marketplace.publicListing,
];

// It's actually 15 minutes but we're refreshing more frequently to ensure the token is always up to date
export const TOKEN_EXPIRY_MINUTES = 5;

export const HEADER_OVERRIDE_PORTAL_ID = 'headerOverrideTarget';

export const PRICE_GRID_EDIT_SECTION_TITLES = {
  livestockType: 'Type of livestock',
  priceAndDelivery: 'Price & delivery information',
  conditions: 'Conditions of purchase',
  livestockSpecs: 'Livestock specifications',
  priceTable: 'Price grid',
};

export const OFFERS_EDIT_SECTION_TITLES = {
  details: 'Offer details',
  priceReference: 'Price reference',
  comments: 'Offer comments',
  delivery: 'Delivery and offer details',
  deliveryInfo: 'Delivery information',
  notes: 'Internal Notes',
};

export const QUERY_KEYS = {
  addresses: 'addresses',
  apiStatus: 'apiStatus',
  booking: 'booking',
  bookingConfirmation: 'bookingConfirmation',
  bookingConfirmationSMS: 'bookingConfirmationSMS',
  bookingHistory: 'bookingHistory',
  bookingStock: 'bookingStock',
  bookings: 'bookings',
  bookingsConfig: 'bookingsConfig',
  config: 'config',
  contact: 'contact',
  contactInviteSearch: 'contactInviteSearch',
  contacts: 'contacts',
  enquiry: 'enquiry',
  enquiryList: 'enquiryList',
  listings: 'listings',
  listingAlert: 'listingAlert',
  listingDetails: 'listingDetails',
  marketplaceConfig: 'marketplaceConfig',
  marketplaceListing: 'marketplaceListing',
  marketplacePublicListing: 'marketplacePublicListing',
  marketplaceOffers: 'marketplaceOffers',
  newOrg: 'newOrg',
  offer: 'offer',
  offers: 'offers',
  org: 'organisation',
  orgs: 'organisations',
  picAttributeList: 'picAttributeList',
  picAutocomplete: 'picAutocomplete',
  picList: 'picList',
  priceGrid: 'priceGrid',
  priceGridEois: 'priceGridEois',
  priceGridListing: 'priceGridListing',
  priceGridOffers: 'priceGridOffers',
  priceGrids: 'priceGrids',
  privateGroup: 'privateGroup',
  privateGroups: 'privateGroups',
  productGrids: 'productGrids',
  products: 'products',
  subscriptions: 'subscriptions',
  summaryGrid: 'summaryGrid',
  summaryGrids: 'summaryGrids',
  user: 'user',
  users: 'users',
  visibilityContacts: 'visibilityContacts',
  visibilityPrivateGroups: 'visibilityPrivateGroups',
  orgMedia: 'orgMedia',
  orgMediaSearch: 'orgMediaSearch',
  bookingRequest: 'bookingRequest',
  bookingRequestSMS: 'bookingRequestSMS',
  orgBookingRequestIncoming: 'orgBookingRequestIncoming',
  orgBookingRequestOutgoing: 'orgBookingRequestOutgoing',
  bookingRequestBuyerOrg: 'bookingRequestBuyerOrg',
  orgUsers: 'orgUsers',
};

export const BUYER_ORG_STORAGE_KEY = 'activeBuyerOrg';

export const FIRST_LOGIN_STORAGE_KEY = 'firstLogin';
export const REDIRECT_URL = 'redirectURL';
export const NAVBAR = 'navbar';

export const APP = 'app';

export const COMMON_PLAN_FEATURES = [
  'Unlimited contacts',
  'Full price grid creation',
  'Broadcasts',
];

export const PUBLIC_CONTACT_PAGE = 'https://agoralivestock.com.au/contact';
export const PUBLIC_TERMS_PAGE = 'https://agoralivestock.com.au/terms-of-use';

export const UPGRADE_MESSAGES = {
  privateGroups:
    'Networks are groups of contacts you can send messages to, such as price changes or new grids. This lets you keep your suppliers are kept up to date with your prices. Networks could include preferred suppliers, certified suppliers or livestock agents.',
};

export const OFFER_REFETCH_INTERVAL = 10000;

export const STATUS_REFETCH_INTERVAL = 60000;

export const USER_MESSAGE = {
  notesVisiblity: '(Not visible to suppliers)',
};

export const cld = new Cloudinary({
  cloud: {
    cloudName: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME,
  },
});

export const FREE_PLAN_FEATURES = {
  target: 'For low-frequency users',
  values: [
    { feature: 'Limited saleyard report access' },
    {
      feature: 'Marketplace access & alerts',
    },
    {
      feature: 'Marketplace fees: 5% sheep/goats, 3.5% cattle',
    },
  ],
};
export const PLANS = [
  {
    plan: 'LITE',
    target: 'For frequent users of free features',
    values: [
      { feature: 'All Free features' },
      {
        feature: 'Full saleyard report access',
      },
    ],
  },
  {
    plan: 'BASE',
    target: 'For producers who want buyer price grids',
    values: [
      { feature: 'All Lite features' },
      {
        feature: 'Price grids from buyers',
      },
    ],
  },
  {
    plan: 'STANDARD',
    target: 'For traders & commercial farmers',
    values: [
      { feature: 'All Base features' },
      {
        feature: 'Market analysis & insights',
      },
      {
        feature: 'Trade Desk access (call/message for in-depth info)',
      },
      {
        feature: 'Reduced marketplace fees: 1% sheep/goats, 0.5% cattle',
      },
    ],
  },
  {
    plan: 'PREMIUM',
    target:
      'For large producers, feedlots & traders (up to 80k sheep or 10k cattle)',
    values: [
      { feature: 'All Standard features' },
      {
        feature: 'Stockbook for booking management & confirmations',
      },
      {
        feature: 'Free “Stock Wanted” marketplace listings',
      },
    ],
  },
  {
    plan: 'AGENT',
    target: 'For livestock agents',
    values: [
      { feature: 'All Premium features' },
      {
        feature: 'No marketplace listing fees',
      },
      {
        feature: 'Website listing embedder (auto-updates your site)',
      },
      {
        feature: 'Option: Full website build for +$500',
      },
    ],
  },
  {
    plan: 'ENTERPRISE',
    target: 'For high-volume organisations (over 80k sheep or 10k cattle)',
    values: [
      { feature: 'All Premium features' },
      {
        feature: 'Stockbook for booking records',
      },
      {
        feature: 'Stockbook integration',
      },
    ],
  },
];
